<template>
    <Layout class="customers-create-page" content-class="content--narrow" is-crm>
        <div class="container">
            <Spinner v-if="loading" />
            <h1 v-t="'crm.customers.create.title'" />
            <form v-if="schema && schema.name" @submit.prevent="submit">
                <Grid>
                    <Column :span="6">
                        <FormField
                            name="name"
                            :label="schema.name.label"
                            :required="schema.name.required"
                            :error="error"
                        >
                            <FormInput
                                v-model="customer.name"
                                :maxlength="schema.name.max_length"
                                :placeholder="schema.name.label"
                            />
                        </FormField>
                    </Column>
                    <Column :span="6">
                        <FormField
                            name="hook"
                            :label="schema.hook.label"
                            :required="schema.hook.required"
                            :error="error"
                        >
                            <FormInput
                                v-model="customer.hook"
                                :maxlength="schema.hook.max_length"
                                :placeholder="schema.hook.label"
                                type="url"
                            />
                        </FormField>
                    </Column>
                </Grid>
                <Grid>
                    <Column :span="6">
                        <FormField
                            name="type_customer"
                            :label="schema.type_customer.label"
                            :required="schema.type_customer.required"
                            :error="error"
                        >
                            <CustomSelect
                                v-model="customer.type_customer"
                                emit-prop="value"
                                view-label="display_name"
                                :form-value-text="({ display_name }) => display_name"
                                :options="schema.type_customer.choices"
                                clearable
                                width="100%"
                            />
                        </FormField>
                    </Column>
                    <Column :span="6">
                        <FormField
                            name="type_productions"
                            :label="schema.type_productions.label"
                            :required="schema.type_productions.required"
                            :error="error"
                        >
                            <CustomSelect
                                v-model="customer.type_productions"
                                emit-prop="value"
                                view-label="display_name"
                                :form-value-text="({ display_name }) => display_name"
                                :options="schema.type_productions.choices"
                                clearable
                                width="100%"
                            />
                        </FormField>
                    </Column>
                </Grid>
                <Grid>
                    <Column :span="6" class="customers-create-page__limits">
                        <FormField
                            name="default_limit"
                            :required="schema.default_limit.required"
                            :error="error"
                        >
                            <template #label>
                                <div class="form-field__label-wrapper">
                                    <label
                                        class="form-field__label"
                                        v-text="schema.default_limit.label"
                                    />
                                    <RadioGroup
                                        v-model="currency"
                                        name="currency"
                                        class="text__radio"
                                        :options="currencies"
                                    />
                                </div>
                            </template>
                            <CustomSelect
                                v-model="customer.default_limit"
                                emit-prop="id"
                                view-label="label"
                                :options="filteredLimits"
                                clearable
                                width="100%"
                            />
                        </FormField>
                    </Column>
                </Grid>
                <Grid>
                    <Column>
                        <FormField name="flows" :required="schema.flows.required" :error="error">
                            <template #label>
                                <h3>{{ schema.flows.label }}</h3>
                            </template>
                            <CustomerFlowsRepeater
                                v-model="customer.flows"
                                :schema="schema.flows.child.children"
                                :flows="flows"
                            />
                        </FormField>
                    </Column>
                </Grid>
                <template v-if="schema.build_settings.child">
                    <Grid :gap-y="1">
                        <Column>
                            <h3>{{ schema.build_settings.label }}</h3>
                        </Column>
                    </Grid>
                    <CustomerBuildSettingsRepeater
                        v-model="customer.build_settings"
                        :schema="schema.build_settings.child.children"
                    />
                </template>
                <Grid v-if="bookLanguages">
                    <Column>
                        <FormField
                            name="default_speakers"
                            :required="schema.default_speakers.required"
                            :error="error"
                        >
                            <template #label>
                                <h3>{{ schema.default_speakers.label }}</h3>
                                <p
                                    v-if="!customer.default_speakers.length"
                                    style="margin-bottom: 20px"
                                >
                                    No speakers yet
                                </p>
                            </template>
                            <CustomerDefaultSpeakersRepeater
                                v-model="customer.default_speakers"
                                :schema="schema.default_speakers.child.children"
                                :book-languages="bookLanguages"
                            />
                        </FormField>
                    </Column>
                </Grid>
                <ErrorList :error="error" />
                <Grid>
                    <Column :span="6">
                        <button
                            :disabled="loading"
                            class="button button--big button--fullwidth button--outlined"
                            @click.prevent="$router.back()"
                            v-text="$t('misc.cancel')"
                        />
                    </Column>
                    <Column :span="6">
                        <button
                            :disabled="!valid"
                            :class="[
                                'button button--big',
                                'button--fullwidth',
                                {
                                    'is-disabled': !valid,
                                    'is-loading': loading,
                                },
                            ]"
                            v-text="$t('misc.save')"
                        />
                    </Column>
                </Grid>
            </form>
        </div>
    </Layout>
</template>

<script>
import { createCustomer } from '../../api/customers';
import { fetchFlows } from '../../../utils/api/statemachine';
import { adminFetchLimits } from '../../../utils/api/payment';
import { fetchBookLanguages } from '@speechki-frontend/core/book/api/books';
import Layout from '@speechki-frontend/shared/ui/layout/Layout';
import Spinner from '@speechki-frontend/shared/ui/Spinner';
import {
    CustomSelect,
    FormField,
    FormInput,
    RadioGroup,
    ErrorList,
} from '@speechki-frontend/shared/ui/forms';
import { Grid, Column } from '@speechki-frontend/shared/ui/Grid';
import { mapState, mapGetters, mapActions } from 'vuex';

import CustomerFlowsRepeater from '../../components/CustomerFlowsRepeater/CustomerFlowsRepeater';
import CustomerBuildSettingsRepeater from '../../components/CustomerBuildSettingsRepeater/CustomerBuildSettingsRepeater';
import CustomerDefaultSpeakersRepeater from '../../components/CustomerDefaultSpeakersRepeater/CustomerDefaultSpeakersRepeater';

export default {
    name: 'CustomersCreatePage',
    components: {
        Layout,
        Spinner,
        CustomSelect,
        FormField,
        FormInput,
        RadioGroup,
        ErrorList,
        Grid,
        Column,
        CustomerFlowsRepeater,
        CustomerBuildSettingsRepeater,
        CustomerDefaultSpeakersRepeater,
    },
    provide() {
        return {
            flows: this.flows,
        };
    },
    data() {
        return {
            loading: false,
            error: undefined,
            flows: [],
            limits: [],
            bookLanguages: undefined,
            currency: null,
            currencies: [
                {
                    id: 'RUB',
                    name: '₽',
                },
                {
                    id: 'USD',
                    name: '$',
                },
            ],
            customer: {
                name: null,
                flows: [],
                default_speakers: [],
                integration_flow: null,
                type_customer: null,
                type_productions: null,
                hook: null,
                default_limit: null,
                build_settings: [
                    {
                        build_types: [],
                        providers: null,
                    },
                ],
            },
        };
    },
    computed: {
        ...mapState('customer', {
            schema: ({ schema }) => schema.create,
        }),
        ...mapGetters(['userCurrency']),
        valid() {
            return Object.entries(this.schema).every(
                ([name, field]) => !field.required || !!this.customer[name]
            );
        },
        filteredLimits() {
            return this.limits.filter(
                ({ currency, price }) => currency == this.currency && +price > 0
            );
        },
    },
    watch: {
        currency() {
            this.customer.default_limit = null;
        },
    },
    async created() {
        this.loading = true;
        try {
            await Promise.all([
                this.fetchCustomersSchema(),
                this.fetchFlows(),
                this.fetchLimits(),
                this.fetchBookLanguages(),
            ]);
        } catch (e) {
            this.error = e;
            console.log(e);
        } finally {
            this.loading = false;
        }

        this.currency = this.userCurrency || this.currencies[0].id;
    },
    methods: {
        ...mapActions('customer', ['fetchCustomersSchema']),

        async fetchFlows() {
            const { results = [] } = await fetchFlows();
            this.flows = results;
        },
        async fetchLimits() {
            const { results = [] } = await adminFetchLimits();
            this.limits = results;
        },
        async fetchBookLanguages() {
            const bookLanguages = await fetchBookLanguages();
            this.bookLanguages = Object.entries(bookLanguages).map(([key, value]) => ({
                id: key,
                name: value,
            }));
        },
        async submit() {
            this.loading = true;
            try {
                await createCustomer(this.customer);
                this.$router.push({
                    name: 'customers',
                    query: { sort: '-id' },
                });
            } catch (e) {
                this.error = e;
                console.log(e);
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style lang="scss" src="./CustomersCreatePage.scss"></style>
